if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) {
          console.error('ProductForm: No form found inside product-form element');
          return;
        }
        
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        
        // Initialize cart reference with retry mechanism
        this.initializeCart();
        
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      initializeCart() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        // If cart not found, wait for DOM to be ready and try again
        if (!this.cart) {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
              this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            });
          } else {
            // DOM is already loaded, try again after a short delay
            setTimeout(() => {
              this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            }, 100);
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Ensure we have a valid variant ID before proceeding
        const variantId = this.variantIdInput?.value;
        if (!variantId) {
          console.error('No variant ID found, cannot add to cart');
          this.handleErrorMessage('Please select a variant');
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Ensure we have a cart element reference
        if (!this.cart) {
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }
        
        if (this.cart) {
          // Handle different cart systems
          if (this.cart.tagName === 'CART-NOTIFICATION') {
            // For cart notification, we need specific sections
            formData.append('sections', 'cart-notification-product,cart-notification-button,cart-icon-bubble');
          } else if (typeof this.cart.getSectionsToRender === 'function') {
            // For cart drawer, use its getSectionsToRender method
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
          } else {
            // Fallback sections
            formData.append('sections', 'cart-drawer,cart-icon-bubble');
          }
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // If no cart element found, try to find it again (might be loaded after this script)
              this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
              if (!this.cart) {
                // Fallback to cart page redirect if no cart drawer/notification found
                console.warn('No cart drawer or notification found, redirecting to cart page');
                window.location = window.routes.cart_url;
                return;
              }
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Handle both cart drawer and cart notification
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              } else {
                // Fallback for cart notification or drawer opening
                const cartNotification = document.querySelector('cart-notification');
                const cartDrawer = document.querySelector('cart-drawer');
                
                if (cartNotification && typeof cartNotification.renderContents === 'function') {
                  console.log('Rendering cart notification with response:', response);
                  cartNotification.renderContents(response);
                } else if (cartDrawer && typeof window.openCartDrawer === 'function') {
                  window.openCartDrawer();
                } else if (cartDrawer && typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                } else {
                  console.error('ProductForm: No cart system found, redirecting to cart page');
                  window.location = window.routes.cart_url || '/cart';
                }
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

// Fallback: Attach event listeners to any cart add forms that might not be inside product-form elements
document.addEventListener('DOMContentLoaded', function() {
  const cartAddForms = document.querySelectorAll('form[action*="/cart/add"]:not(product-form form)');
  
  cartAddForms.forEach(form => {
    form.addEventListener('submit', function(evt) {
      evt.preventDefault();
      
      const formData = new FormData(form);
      const variantId = formData.get('id');
      
      if (!variantId) {
        console.error('Fallback: No variant ID found');
        return;
      }
      
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: formData.get('quantity') || 1,
          sections: 'cart-drawer,cart-notification-product,cart-icon-bubble'
        })
      })
      .then(response => response.json())
      .then(data => {
        // Try cart notification first, then cart drawer
        const cartNotification = document.querySelector('cart-notification');
        const cartDrawer = document.querySelector('cart-drawer');
        
        if (cartNotification && typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(data);
        } else if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else if (cartDrawer) {
          cartDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        } else {
          window.location = '/cart';
        }
      })
      .catch(error => {
        console.error('Fallback: Add to cart failed', error);
        window.location = '/cart';
      });
    });
  });
});
